import { useRef, useState, useMemo } from 'react'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { useLanguage } from '../context/LanguageContext'

/**
 * Shared rich-text helpers. Originally lived inside BrandProfile's "How it
 * works" section; extracted so the bundle + Stay & Play package descriptions
 * use the exact same editor (toolbar + EN/FR tabs + preview) for a consistent
 * authoring experience.
 */

// Render markdown to sanitized HTML (same options as the editor preview).
export function mdToHtml(text) {
  return DOMPurify.sanitize(marked.parse(text || '', { breaks: true }))
}

// Flatten markdown to plain text — used for short card snippets where rendering
// full HTML (and truncating it) would be awkward.
export function stripMarkdown(s) {
  if (!s) return s
  return String(s)
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')      // images
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')   // links -> text
    .replace(/`{1,3}([^`]*)`{1,3}/g, '$1')     // code
    .replace(/^#{1,6}\s+/gm, '')               // headings
    .replace(/^\s*[-*+]\s+/gm, '')             // bullet markers
    .replace(/^\s*\d+\.\s+/gm, '')             // numbered markers
    .replace(/\*\*([^*]+)\*\*/g, '$1')         // bold
    .replace(/\*([^*]+)\*/g, '$1')             // italic *
    .replace(/_([^_]+)_/g, '$1')               // italic _
    .replace(/^>\s?/gm, '')                     // blockquote
    .replace(/\s*\n+\s*/g, ' ')                // collapse newlines
    .replace(/\s+/g, ' ')
    .trim()
}

function ToolBtn({ onClick, title, children }) {
  return <button type="button" onClick={onClick} title={title} style={{ padding: '4px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13, cursor: 'pointer', lineHeight: 1.4 }}>{children}</button>
}

/**
 * Lightweight markdown editor with a B/I/H/List/Link/Preview toolbar.
 * `placeholder` overrides the default authoring hint.
 */
export function MarkdownEditor({ value, onChange, placeholder, insertTokens }) {
  const { t } = useLanguage()
  const ref = useRef(null)
  const [preview, setPreview] = useState(false)
  const html = useMemo(() => mdToHtml(value), [value])

  function surround(before, after) {
    const ta = ref.current; if (!ta) return
    const s = ta.selectionStart, e = ta.selectionEnd
    const next = value.slice(0, s) + before + value.slice(s, e) + after + value.slice(e)
    onChange(next)
    requestAnimationFrame(() => { ta.focus(); ta.selectionStart = s + before.length; ta.selectionEnd = e + before.length })
  }
  // Drop a token (e.g. a {{placeholder}}) in at the cursor.
  function insertAtCursor(text) {
    const ta = ref.current
    if (!ta) { onChange((value || '') + text); return }
    const s = ta.selectionStart, e = ta.selectionEnd
    onChange(value.slice(0, s) + text + value.slice(e))
    requestAnimationFrame(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = s + text.length })
  }
  function prefixLines(prefix) {
    const ta = ref.current; if (!ta) return
    const s = ta.selectionStart, e = ta.selectionEnd
    const start = value.lastIndexOf('\n', s - 1) + 1
    const block = value.slice(start, e) || ''
    const replaced = block.split('\n').map(l => prefix + l).join('\n')
    onChange(value.slice(0, start) + replaced + value.slice(e))
  }

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 4, padding: 6, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        <ToolBtn onClick={() => surround('**', '**')} title={t('sp_md_bold', 'Bold')}><b>B</b></ToolBtn>
        <ToolBtn onClick={() => surround('_', '_')} title={t('sp_md_italic', 'Italic')}><i>I</i></ToolBtn>
        <ToolBtn onClick={() => surround('## ', '')} title={t('sp_md_heading', 'Heading')}>H</ToolBtn>
        <ToolBtn onClick={() => prefixLines('- ')} title={t('sp_md_bulleted_list', 'Bulleted list')}>{t('sp_md_btn_list', '• List')}</ToolBtn>
        <ToolBtn onClick={() => prefixLines('1. ')} title={t('sp_md_numbered_list', 'Numbered list')}>{t('sp_md_btn_numbered_list', '1. List')}</ToolBtn>
        <ToolBtn onClick={() => surround('[', '](https://)')} title={t('sp_md_link', 'Link')}>{t('sp_md_link', 'Link')}</ToolBtn>
        {insertTokens && insertTokens.length > 0 && (
          <>
            <span style={{ width: 1, alignSelf: 'stretch', background: '#e2e8f0', margin: '0 3px' }} />
            {insertTokens.map(tk => (
              <ToolBtn key={tk.token} onClick={() => insertAtCursor(tk.token)} title={t('sp_md_insert', 'Insert') + ' ' + tk.label}>+ {tk.label}</ToolBtn>
            ))}
          </>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <ToolBtn onClick={() => setPreview(p => !p)} title={t('sp_md_toggle_preview', 'Toggle preview')}>{preview ? t('edit', 'Edit') : t('preview', 'Preview')}</ToolBtn>
        </div>
      </div>
      {preview
        ? <div className="hiw-preview" style={{ padding: 12, minHeight: 120, fontSize: 14, lineHeight: 1.5, color: '#0f172a' }} dangerouslySetInnerHTML={{ __html: html }} />
        : <textarea
            ref={ref}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder || t('sp_md_placeholder', 'Describe how booking works. **bold**, _italic_, - lists, [links](https://...)')}
            style={{ width: '100%', minHeight: 140, padding: 12, border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
          />}
    </div>
  )
}

const tabStyle = (active) => ({ padding: '6px 14px', background: active ? '#0f172a' : '#fff', color: active ? '#fff' : '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' })

/**
 * English/Français tab pair over two MarkdownEditors — the customer embed shows
 * whichever language the viewer is browsing in. Mirrors the brand-profile
 * "How it works" pattern exactly.
 */
export function BilingualMarkdownEditor({ valueEn, valueFr, onChangeEn, onChangeFr, placeholderEn, placeholderFr, insertTokens, lang: controlledLang, onLangChange }) {
  const { t } = useLanguage()
  const [internalLang, setInternalLang] = useState('en')
  // Optionally controlled: when a parent passes `lang`, it drives the language
  // (e.g. a single toggle shared with a sibling field) and renders its own
  // EN/FR switch, so we hide ours. Uncontrolled = self-managed tabs (default).
  const controlled = controlledLang !== undefined
  const lang = controlled ? controlledLang : internalLang
  const setLang = controlled ? (onLangChange || (() => {})) : setInternalLang
  return (
    <>
      {!controlled && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <button type="button" onClick={() => setLang('en')} style={tabStyle(lang === 'en')}>{t('english', 'English')}</button>
          <button type="button" onClick={() => setLang('fr')} style={tabStyle(lang === 'fr')}>{t('french', 'Français')}</button>
        </div>
      )}
      {lang === 'en'
        ? <MarkdownEditor value={valueEn || ''} onChange={onChangeEn} placeholder={placeholderEn} insertTokens={insertTokens} />
        : <MarkdownEditor value={valueFr || ''} onChange={onChangeFr} placeholder={placeholderFr} insertTokens={insertTokens} />}
    </>
  )
}
